/**
 * PaymentReceipt — Unified receipt card for completed payments.
 *
 * Renders from structured metadata (payment_receipt message type).
 * Supports both Stripe and USDC rails.
 *
 * Metadata shape:
 *   amount_display  — "9.00 USDC" or "$9.00"
 *   payment_rail    — "stripe" or "usdc"
 *   payment_type    — "build", "hosting", "domain"
 *   tx_hash         — USDC only, for BaseScan link
 *   card_description — Stripe only, e.g. "Visa ending in 4242"
 *   owing_cents      — a partial payment (Link 20 D4): "Payment received", with what's still owing
 *   total_cents      — the payment that completed an order paid in parts: "Paid in full", with the total
 */

import React from 'react';
import '../styles/PaymentReceipt.css';

const BASESCAN_TX_URL = 'https://basescan.org/tx/';

function formatReceiptDate(dateString) {
  if (!dateString) return '';
  let isoString = dateString;
  if (!isoString.endsWith('Z') && !isoString.match(/[+-]\d{2}(:\d{2})?$/)) {
    isoString = isoString + 'Z';
  }
  return new Date(isoString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function PaymentReceipt({ metadata, createdAt }) {
  const {
    amount_display,
    payment_rail,
    tx_hash,
    card_description,
    owing_cents,
    total_cents,
  } = metadata || {};

  const isUsdc = payment_rail === 'usdc';
  const money = (cents) => (isUsdc ? `${(cents / 100).toFixed(2)} USDC` : `$${(cents / 100).toFixed(2)}`);
  const label = owing_cents ? 'Payment received' : total_cents ? 'Paid in full' : 'Paid';

  const methodLabel = payment_rail === 'usdc'
    ? 'USDC (Base)'
    : card_description || 'Stripe (card)';

  const dateDisplay = formatReceiptDate(createdAt);

  return (
    <div className="payment-receipt">
      <div className="receipt-check">{owing_cents ? '🧾' : '✅'}</div>
      <div className="receipt-label">{label}</div>
      <div className="receipt-amount">{amount_display}</div>
      {owing_cents ? <div className="receipt-owing">{money(owing_cents)} still owing</div> : null}
      {!owing_cents && total_cents ? <div className="receipt-owing">{money(total_cents)} total</div> : null}
      <div className="receipt-meta">
        {dateDisplay && <span>{dateDisplay}</span>}
        {dateDisplay && <span className="receipt-dot">·</span>}
        <span>{methodLabel}</span>
      </div>
      <div className="receipt-links">
        {payment_rail === 'usdc' && tx_hash && (
          <a
            href={`${BASESCAN_TX_URL}${tx_hash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="receipt-link"
          >
            View on BaseScan ↗
          </a>
        )}
        <a href="/profile?tab=payments" className="receipt-link">
          View Payments →
        </a>
      </div>
    </div>
  );
}
